import { supabase } from '@/integrations/supabase/client';
import { MonthlyMetric } from './monthlyMetricsService';

/**
 * Получает данные о новых клиентах по месяцам
 */
export const getMonthlyNewCustomers = async (monthsCount: number): Promise<MonthlyMetric[]> => {
  try {
    // Получаем ВСЕ успешные платежи больше 2000 рублей
    const { data: allPaymentsData, error: allError } = await supabase
      .from('payments')
      .select('courier_id, payment_date, status, amount')
      .eq('status', 'paid')
      .gt('amount', 2000)
      .order('payment_date', { ascending: true });

    if (allError || !allPaymentsData) {
      console.error('Ошибка при получении всех данных о платежах:', allError);
      return [];
    }

    // Подсчитываем общее количество платежей для каждого курьера
    const courierTotalPayments = new Map<string, number>();
    allPaymentsData.forEach(payment => {
      if (payment.courier_id) {
        courierTotalPayments.set(
          payment.courier_id, 
          (courierTotalPayments.get(payment.courier_id) || 0) + 1
        );
      }
    });

    // Определяем период для анализа
    const startDate = new Date(Date.now() - monthsCount * 30 * 24 * 60 * 60 * 1000);

    // Группируем новых клиентов по месяцам (курьеры с РОВНО 1 платежом за всю историю)
    const monthlyNewCustomers = new Map<string, number>();
    allPaymentsData.forEach(payment => {
      if (payment.payment_date && payment.courier_id) {
        const totalPayments = courierTotalPayments.get(payment.courier_id) || 0;
        
        // Новый клиент = ровно 1 платеж за всю историю
        if (totalPayments === 1) {
          const paymentDate = new Date(payment.payment_date);
          
          // Учитываем только платежи в анализируемом периоде
          if (paymentDate >= startDate) {
            const monthKey = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
            monthlyNewCustomers.set(monthKey, (monthlyNewCustomers.get(monthKey) || 0) + 1);
          }
        }
      }
    });

    // Преобразуем в массив метрик
    const metrics: MonthlyMetric[] = [];
    monthlyNewCustomers.forEach((value, monthKey) => {
      const [year, month] = monthKey.split('-').map(Number);
      metrics.push({
        year,
        month,
        value,
        growth: 0
      });
    });

    // Сортируем по дате
    metrics.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    // Рассчитываем рост по сравнению с предыдущим месяцем
    for (let i = 1; i < metrics.length; i++) {
      const current = metrics[i].value;
      const previous = metrics[i - 1].value;
      metrics[i].previousValue = previous;
      if (previous > 0) {
        metrics[i].growth = Math.round(((current - previous) / previous) * 100);
      }
    }

    return metrics;
  } catch (error) {
    console.error('Ошибка при получении месячных данных о новых клиентах:', error);
    return [];
  }
};

/**
 * Получает данные о повторных клиентах по месяцам
 */
export const getMonthlyRepeatCustomers = async (monthsCount: number): Promise<MonthlyMetric[]> => {
  try {
    // Получаем ВСЕ успешные платежи больше 2000 рублей
    const { data: allPaymentsData, error: allError } = await supabase
      .from('payments')
      .select('courier_id, payment_date, status, amount')
      .eq('status', 'paid')
      .gt('amount', 2000)
      .order('payment_date', { ascending: true });

    if (allError || !allPaymentsData) {
      console.error('Ошибка при получении всех данных о платежах:', allError);
      return [];
    }

    // Подсчитываем общее количество платежей для каждого курьера
    const courierTotalPayments = new Map<string, number>();
    allPaymentsData.forEach(payment => {
      if (payment.courier_id) {
        courierTotalPayments.set(
          payment.courier_id, 
          (courierTotalPayments.get(payment.courier_id) || 0) + 1
        );
      }
    });

    // Определяем период для анализа
    const startDate = new Date(Date.now() - monthsCount * 30 * 24 * 60 * 60 * 1000);

    // Группируем повторных клиентов по месяцам (курьеры с БОЛЕЕ чем 1 платежом за всю историю)
    const monthlyRepeatCustomers = new Map<string, Set<string>>();
    
    allPaymentsData.forEach(payment => {
      if (payment.payment_date && payment.courier_id) {
        const totalPayments = courierTotalPayments.get(payment.courier_id) || 0;
        
        // Повторный клиент = более 1 платежа за всю историю
        if (totalPayments > 1) {
          const paymentDate = new Date(payment.payment_date);
          
          // Учитываем только платежи в анализируемом периоде
          if (paymentDate >= startDate) {
            const monthKey = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
            
            if (!monthlyRepeatCustomers.has(monthKey)) {
              monthlyRepeatCustomers.set(monthKey, new Set());
            }
            // Используем Set для подсчета уникальных курьеров в месяце
            monthlyRepeatCustomers.get(monthKey)!.add(payment.courier_id);
          }
        }
      }
    });

    // Преобразуем в массив метрик
    const metrics: MonthlyMetric[] = [];
    monthlyRepeatCustomers.forEach((courierSet, monthKey) => {
      const [year, month] = monthKey.split('-').map(Number);
      metrics.push({
        year,
        month,
        value: courierSet.size,
        growth: 0
      });
    });

    // Сортируем по дате
    metrics.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    // Рассчитываем рост по сравнению с предыдущим месяцем
    for (let i = 1; i < metrics.length; i++) {
      const current = metrics[i].value;
      const previous = metrics[i - 1].value;
      metrics[i].previousValue = previous;
      if (previous > 0) {
        metrics[i].growth = Math.round(((current - previous) / previous) * 100);
      }
    }

    return metrics;
  } catch (error) {
    console.error('Ошибка при получении месячных данных о повторных клиентах:', error);
    return [];
  }
};